import { useCallback, useEffect, useState } from 'react';
import {
  COLUMN_MIN_WIDTH,
  FROZEN_NAME_DEFAULT_WIDTH,
  SCREENER_COLUMN_DEFS,
  SCREENER_RIGHT_COLUMN_IDS,
  defaultScreenerColumnState,
  type ScreenerColumnState,
  type ScreenerRightColumnId,
} from './screener-columns.js';

// Per-panel column-layout state for the new platform-wide Screener.
// Persisted to localStorage under a versioned schema so a future change
// to the column set won't poison the saved state. On version mismatch
// or any parse error we fall back to defaults.

const SCHEMA_VERSION = 1;

export interface PersistedScreenerColumnConfig {
  v: number;
  nameWidth: number;
  columns: ScreenerColumnState[];
}

export interface ScreenerColumnConfig {
  nameWidth: number;
  columns: ScreenerColumnState[];
}

export interface ScreenerColumnConfigActions {
  setNameWidth: (px: number) => void;
  setColumnWidth: (id: ScreenerRightColumnId, px: number) => void;
  setColumnVisible: (id: ScreenerRightColumnId, visible: boolean) => void;
  moveColumn: (id: ScreenerRightColumnId, targetIndex: number) => void;
  resetToDefaults: () => void;
}

export function useScreenerColumnConfig(
  storageKey: string,
): [ScreenerColumnConfig, ScreenerColumnConfigActions] {
  const [config, setConfig] = useState<ScreenerColumnConfig>(() => loadOrDefault(storageKey));

  useEffect(() => {
    try {
      const payload: PersistedScreenerColumnConfig = {
        v: SCHEMA_VERSION,
        nameWidth: config.nameWidth,
        columns: config.columns,
      };
      localStorage.setItem(storageKey, JSON.stringify(payload));
    } catch {
      // Ignore — non-critical.
    }
  }, [config, storageKey]);

  const setNameWidth = useCallback((px: number) => {
    setConfig((c) => ({ ...c, nameWidth: Math.max(COLUMN_MIN_WIDTH, Math.round(px)) }));
  }, []);

  const setColumnWidth = useCallback((id: ScreenerRightColumnId, px: number) => {
    const width = Math.max(COLUMN_MIN_WIDTH, Math.round(px));
    setConfig((c) => ({
      ...c,
      columns: c.columns.map((col) => (col.id === id ? { ...col, width } : col)),
    }));
  }, []);

  const setColumnVisible = useCallback((id: ScreenerRightColumnId, visible: boolean) => {
    setConfig((c) => ({
      ...c,
      columns: c.columns.map((col) => (col.id === id ? { ...col, visible } : col)),
    }));
  }, []);

  const moveColumn = useCallback((id: ScreenerRightColumnId, targetIndex: number) => {
    setConfig((c) => {
      const fromIndex = c.columns.findIndex((col) => col.id === id);
      if (fromIndex === -1) return c;
      const clamped = Math.max(0, Math.min(c.columns.length - 1, targetIndex));
      if (fromIndex === clamped) return c;
      const next = [...c.columns];
      const [moved] = next.splice(fromIndex, 1);
      if (!moved) return c;
      next.splice(clamped, 0, moved);
      return { ...c, columns: next };
    });
  }, []);

  const resetToDefaults = useCallback(() => {
    setConfig({
      nameWidth: FROZEN_NAME_DEFAULT_WIDTH,
      columns: defaultScreenerColumnState(),
    });
  }, []);

  return [config, { setNameWidth, setColumnWidth, setColumnVisible, moveColumn, resetToDefaults }];
}

function loadOrDefault(storageKey: string): ScreenerColumnConfig {
  const defaults: ScreenerColumnConfig = {
    nameWidth: FROZEN_NAME_DEFAULT_WIDTH,
    columns: defaultScreenerColumnState(),
  };
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return defaults;
    const parsed = JSON.parse(raw) as Partial<PersistedScreenerColumnConfig>;
    if (parsed.v !== SCHEMA_VERSION) return defaults;
    if (typeof parsed.nameWidth !== 'number') return defaults;
    if (!Array.isArray(parsed.columns)) return defaults;
    const known = new Set<ScreenerRightColumnId>(SCREENER_RIGHT_COLUMN_IDS);
    const stored = parsed.columns.filter(
      (c): c is ScreenerColumnState =>
        typeof c === 'object' &&
        c !== null &&
        typeof c.id === 'string' &&
        known.has(c.id as ScreenerRightColumnId) &&
        typeof c.width === 'number' &&
        typeof c.visible === 'boolean',
    );
    const storedIds = new Set(stored.map((c) => c.id));
    // Insert any newly-added columns at their CANONICAL position — right after the
    // nearest preceding canonical column already present — rather than appending to
    // the end. Keeps the user's existing order, but lands a new column next to its
    // logical neighbour (e.g. Quality trajectory beside Quality tier) instead of far right.
    const columns: ScreenerColumnState[] = [...stored];
    for (const id of SCREENER_RIGHT_COLUMN_IDS) {
      if (storedIds.has(id)) continue;
      const col: ScreenerColumnState = {
        id,
        width: SCREENER_COLUMN_DEFS[id].defaultWidth,
        visible: true,
      };
      const canonIdx = SCREENER_RIGHT_COLUMN_IDS.indexOf(id);
      let insertAt = columns.length;
      for (let i = canonIdx - 1; i >= 0; i--) {
        const pos = columns.findIndex((c) => c.id === SCREENER_RIGHT_COLUMN_IDS[i]);
        if (pos !== -1) {
          insertAt = pos + 1;
          break;
        }
      }
      columns.splice(insertAt, 0, col);
    }
    return {
      nameWidth: Math.max(COLUMN_MIN_WIDTH, parsed.nameWidth),
      columns,
    };
  } catch {
    return defaults;
  }
}
